import { Box, Chip, Tooltip } from '@mui/joy';
import { CheckCircle, Clock, PhoneOutgoing } from '@phosphor-icons/react';
import type { Lead } from '../../../types/admin';

function outreachAge(timestamp: number, now: number): string {
  const minutes = Math.floor(Math.max(0, now - timestamp) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const KINDS = { call: 'Call logged', meeting: 'Meeting logged', email: 'Outbound email' };

export function LeadOutreachIndicator({
  lead,
  now,
}: {
  lead: Pick<Lead, 'last_outreach_at' | 'last_outreach_kind'>;
  now: number;
}) {
  const timestamp = lead.last_outreach_at ? Date.parse(lead.last_outreach_at) : NaN;
  // Missing fields can occur during a rolling frontend/backend deployment.
  // Do not label an unknown history as a lead nobody has contacted.
  if (
    lead.last_outreach_at === undefined ||
    (lead.last_outreach_at !== null && !Number.isFinite(timestamp))
  ) {
    return (
      <Chip size="sm" variant="outlined" color="neutral">
        Outreach unavailable
      </Chip>
    );
  }
  if (lead.last_outreach_at === null) {
    return (
      <Tooltip title="No recorded call, meeting, or outbound personal email. Notes, inbound messages, and automated emails do not count as initial outreach.">
        <Chip size="sm" variant="soft" color="warning" startDecorator={<PhoneOutgoing size={14} />}>
          Needs outreach
        </Chip>
      </Tooltip>
    );
  }
  const detail = `${KINDS[lead.last_outreach_kind ?? 'email']} · ${new Date(timestamp).toLocaleString()}`;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title="A call, meeting, or outbound personal email is recorded. This does not confirm a reply or a successful conversation.">
        <Chip size="sm" variant="soft" color="success" startDecorator={<CheckCircle size={14} />}>
          Contacted
        </Chip>
      </Tooltip>
      <Tooltip title={detail}>
        <Chip
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<Clock size={14} />}
          aria-label={`Last outreach: ${detail}`}
        >
          Last outreach {outreachAge(timestamp, now)}
        </Chip>
      </Tooltip>
    </Box>
  );
}
